import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import type {
  UseCurrentSubscriptionQuery,
  UseCurrentSubscriptionQueryVariables,
} from 'src/lib/graphqlTypes/types';

export const USE_CURRENT_SUBSCRIPTION_QUERY = gql<
  UseCurrentSubscriptionQuery,
  UseCurrentSubscriptionQueryVariables
>/* GraphQL */ `
  query UseCurrentSubscriptionQuery(
    $accountId: ID!
    $canQueryBilling: Boolean!
  ) {
    account(id: $accountId) {
      id
      isLocked
      currentSubscription @include(if: $canQueryBilling) {
        id: uuid
        canceledAt
        autoRenew
        pricePerSeatInUsdCents
        pricePerUnitInUsdCents
        currentPeriodStartedAt
        currentPeriodEndsAt
        state
        trialExpiresAt
      }
      currentPlan {
        id
        readableId
      }
    }
  }
`;

export function useCurrentSubscription({
  skip = false,
  ...variables
}: { skip?: boolean } & UseCurrentSubscriptionQueryVariables) {
  const { data: permissionsData } = usePermissions(
    variables.accountId,
    undefined,
    undefined,
    skip || !variables.accountId,
  );
  const canQueryBilling =
    !!permissionsData?.accountPermissions?.canQueryBillingInfo;

  const currentSubscriptionResult = useQuery(USE_CURRENT_SUBSCRIPTION_QUERY, {
    variables: {
      ...variables,
      canQueryBilling,
    },
    skip,
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  return useMemo(() => {
    return {
      currentSubscription:
        currentSubscriptionResult.data?.account?.currentSubscription,
      isLocked: !!currentSubscriptionResult.data?.account?.isLocked,
      ...currentSubscriptionResult,
    };
  }, [currentSubscriptionResult]);
}
